import { captureException } from '@sentry/react';
import { NotificationDispatch } from '@tryhackme/thm-ui-components';
import { useCallback, useMemo, useState } from 'react';

import {
  useAddSavedRoomMutation,
  useGetRoomsQuery,
  useRemoveSavedRoomMutation,
} from '../../features/my-rooms/my-rooms.slice';

interface UseSavedRoomsReturn {
  savedRooms: string[];
  loadingRoomCode: string | null;
  handleSavedRoom: (roomCode: string) => Promise<void>;
  refetchSavedRooms: () => void;
  isAuth: boolean;
}

export const useSavedRooms = ({ isAuth }: { isAuth: boolean }): UseSavedRoomsReturn => {
  const [loadingRoomCode, setLoadingRoomCode] = useState<string | null>(null);
  const [addSavedRoom] = useAddSavedRoomMutation();
  const [removeSavedRoom] = useRemoveSavedRoomMutation();

  const { data: savedRoomsData, refetch: refetchSavedRooms } = useGetRoomsQuery(
    { type: 'saved', searchText: '', isRoomCompleted: true, page: 1, limit: 300 },
    { skip: !isAuth },
  );

  const savedRooms = useMemo(() => savedRoomsData?.data?.docs?.map((room) => room.code) ?? [], [savedRoomsData]);

  const handleSavedRoom = useCallback(
    async (roomCode: string) => {
      const isCurrentlySaved = savedRooms.includes(roomCode);

      try {
        setLoadingRoomCode(roomCode);

        const mutation = isCurrentlySaved ? removeSavedRoom : addSavedRoom;
        await mutation(roomCode).unwrap();

        refetchSavedRooms();

        NotificationDispatch('success', `Success! ${isCurrentlySaved ? 'Removed from' : 'Saved to'} My Rooms`);
      } catch (error) {
        captureException(error, {
          tags: {
            roomCode,
            action: isCurrentlySaved ? 'remove' : 'add',
          },
          extra: {
            timestamp: new Date().toISOString(),
          },
        });

        NotificationDispatch('error', 'Something went wrong. Please try again.');
      } finally {
        setLoadingRoomCode(null);
      }
    },
    [savedRooms, addSavedRoom, removeSavedRoom, refetchSavedRooms],
  );

  return {
    isAuth,
    savedRooms,
    loadingRoomCode,
    handleSavedRoom,
    refetchSavedRooms,
  };
};
